import { Banner } from "@foodpilot/foods";
import { Grid, styled } from "@mui/material";
import { useScoreDetailContext } from "src/context/ScoreDetail/useScoreDetailContext";
import { getScoreDetailRemoteState } from "src/context/ScoreDetail/scoreDetailRemoteState";
import { ComparisonCard, ComparisonData } from "./Common/ComparisonCard";
import { SummaryLayout } from "./SummaryLayout";
import { useEffect } from "react";
import { Loading } from "src/components/utils/Loading";
import { ScoreQueryError } from "src/components/ScoreState/ScoreQueryError";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { useTranslation } from "react-i18next";
import type { ComponentAppearance } from "src/components/appearance";

type ComparisonChartProps = { appearance?: ComponentAppearance };

export const ComparisonChart = ({ appearance = "legacy" }: ComparisonChartProps) => {
    const { t } = useTranslation();
    const isProductUxV2 = appearance === "product-v2";

    const {
        olapScores,
        scoresParams,
        areAggregationsRequested,
        getDimensionValueLocalizedLabel,
        getDimensionValueDrilldownAction,
        isLoading,
        scoreQueryState,
        scoreQueryError,
        retryScoreQuery,
        drilldownState,
    } = useScoreDetailContext();
    const resolvedScoreQueryState =
        scoreQueryState ??
        getScoreDetailRemoteState({
            score: olapScores,
            isLoading: isLoading ?? false,
            error: scoreQueryError,
        });

    useEffect(() => {
        scoresParams?.state.setQuery({
            scores: {
                ...scoresParams.query.scores,
                aggregations: ["min", "max", "avg", "median", "count"],
            },
        });
    }, []);

    if (isLoading) {
        return <Loading appearance={appearance} />;
    }

    if (
        isProductUxV2 &&
        (resolvedScoreQueryState.status === "contract-error" ||
            resolvedScoreQueryState.status === "forbidden" ||
            resolvedScoreQueryState.status === "not-found" ||
            resolvedScoreQueryState.status === "server-error")
    ) {
        return <ScoreQueryError context="details" onRetry={retryScoreQuery} status={resolvedScoreQueryState.status} />;
    }

    if (!areAggregationsRequested) {
        return <Loading appearance={appearance} />;
    }

    if (isProductUxV2 && !olapScores) {
        return <Banner variant="info">{t("product.score_details.no_data", "No score details available")}</Banner>;
    }

    if (!olapScores) return <Loading appearance={appearance} />;

    const total = {
        key: olapScores.label || t("score_detail.total", "Total"),
        title: olapScores.label || t("score_detail.total", "Total"),
        score: {
            value: OlapScoresService.normalizeNumericalScore(olapScores.score.value),
            min: OlapScoresService.normalizeNumericalScore(olapScores.score.min),
            max: OlapScoresService.normalizeNumericalScore(olapScores.score.max),
            avg: OlapScoresService.normalizeNumericalScore(olapScores.score.avg),
            median: OlapScoresService.normalizeNumericalScore(olapScores.score.median),
            count: OlapScoresService.normalizeNumericalScore(olapScores.score.count),
        },
    };

    const comparisonCardTotal: ComparisonData = {
        key: total.title,
        title: total.title,
        min: total.score.min,
        max: total.score.max,
        average: total.score.avg,
        median: total.score.median,
        count: total.score.count,
        value: total.score.value,
    };

    const unit = olapScores.unit;
    const dimensions = OlapScoresService.getDimensionValueDimensions(
        olapScores,
        drilldownState?.last?.type,
        drilldownState?.last?.id,
    );
    const dimensionValuesPerDimension = OlapScoresService.flattenDimensionValuesPerDimension(dimensions);

    const comparisonCardData =
        dimensionValuesPerDimension ?
            Object.entries(dimensionValuesPerDimension ?? {}).reduce<ComparisonData[]>(
                (acc, [dimension, dimensionValues]) => {
                    dimensionValues.map((dimensionValue) => {
                        const score = dimensionValue.score;

                        if (typeof score.value !== "number") return;

                        const label =
                            getDimensionValueLocalizedLabel?.(dimension, dimensionValue.id) ?? dimensionValue.label;
                        const action = getDimensionValueDrilldownAction?.(dimension, dimensionValue);

                        acc.push({
                            key: label,
                            title: label,
                            min: OlapScoresService.normalizeNumericalScore(score.min),
                            max: OlapScoresService.normalizeNumericalScore(score.max),
                            average: OlapScoresService.normalizeNumericalScore(score.avg),
                            median: OlapScoresService.normalizeNumericalScore(score.median),
                            count: OlapScoresService.normalizeNumericalScore(score.count),
                            value: OlapScoresService.normalizeNumericalScore(score.value),
                            action: dimensionValue.dimensions.length > 0 ? action : undefined,

                            noData: false,
                            noDimensions: false,
                        });
                    });

                    return acc;
                },
                [],
            )
        :   [];

    if (drilldownState.state.length === 0) {
        const items = [comparisonCardTotal, ...comparisonCardData];
        return <SummaryLayout appearance={appearance} items={items} unit={unit} />;
    }

    return (
        <ComparisonScoreGrid container spacing={2}>
            {comparisonCardData.map((score) => {
                return (
                    <Grid item xs={12} md={4} key={score.title}>
                        <ComparisonCard score={score} unit={unit} />
                    </Grid>
                );
            })}
        </ComparisonScoreGrid>
    );
};

const ComparisonScoreGrid = styled(Grid)<{ direction?: "row" | "column" }>(({ theme, direction = "row" }) => ({
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: direction,
    padding: theme.spacing(2),
    height: "100%",
    width: "100%",
}));
